/**
 * TodayWithMate — Dashboard panel showing Mate's structured action queue
 * Phase 2: Progress mode — categorised buckets with one-click actions
 * Phase 3: Patrol alerts integrated
 *
 * Credit Control bucket: ACCOUNTS role only.
 * FIX (13 Apr 2026): Removed ROLES.DIRECTOR from canSeeCreditControl.
 * Directors don't need CC data in their Today card — that's May's domain.
 * All other users see Deadlines, Next Steps, My Matters, Stale, Completions.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { SUPERVISED_FEE_EARNERS, getUserFeeEarner, getUserRole, ROLES } from '../config/roleConfig';
import {
  Sparkles, AlertTriangle, Clock, Calendar, PoundSterling,
  Loader2, ChevronRight, Shield, FileText, RefreshCw, X,
  CheckCircle, AlertCircle, Zap, Briefcase, GraduationCap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fetchComplianceTasks } from "./todayCompliance";
import { buildTodayActions, URGENCY } from "./todayActions";
import { matterPagePath, openPipelineAction } from "../utils/openPipelineAction";

const userId = () => sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
const headers = () => ({ "X-Bonsai-User-Id": userId() });

const SEVERITY_DOT = { critical: "bg-status-red", high: "bg-status-red", medium: "bg-status-amber", low: "bg-teal" };

function BucketHeader({ icon: Icon, iconColor, title, count, badge }) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-md flex items-center justify-center cm-glass ring-1 ring-white/10">
          <Icon className={`w-3 h-3 ${iconColor}`} />
        </span>
        <span className="text-[11px] font-semibold text-white">{title}</span>
      </div>
      {count > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${badge}`}>{count}</span>}
    </div>
  );
}

function ActionRow({ text, detail, severity, onClick }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-navy-hover/60 transition-colors text-left group" data-testid="today-action-row">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${SEVERITY_DOT[severity] || SEVERITY_DOT.medium}`} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-white truncate">{text}</p>
        {detail && <p className="text-[9px] text-slate-400 truncate">{detail}</p>}
      </div>
      <ChevronRight className="w-3 h-3 text-slate-600 group-hover:text-slate-400 shrink-0" />
    </button>
  );
}

// BM-H3775: one row per matter. The bar and the first chip carry the
// urgency; the other chips are the matter's other outstanding steps.
const URGENCY_BAR = {
  [URGENCY.overdue]: "bg-status-red", [URGENCY.stopped]: "bg-status-red",
  [URGENCY.next]: "bg-status-amber", [URGENCY.waiting]: "bg-slate-600",
};
const URGENCY_CHIP = {
  [URGENCY.overdue]: "border-status-red/60 text-status-red bg-status-red-dim",
  [URGENCY.stopped]: "border-status-red/60 text-status-red bg-status-red-dim",
  [URGENCY.next]: "border-status-amber/60 text-status-amber bg-status-amber-dim",
  [URGENCY.waiting]: "border-navy-border text-slate-400 bg-navy/60",
};
const QUIET_CHIP = "border-navy-border text-slate-300 bg-navy/60 hover:border-teal/60 hover:text-white";

function MatterActionRow({ matter, onOpen }) {
  const [first, ...rest] = matter.steps;
  if (!first) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(first, matter)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(first, matter); }}
      className="relative w-full flex items-start gap-3 pl-3.5 pr-2 py-2 rounded hover:bg-navy-hover/60 transition-colors text-left cursor-pointer group"
      data-testid="today-matter-row"
    >
      <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full ${URGENCY_BAR[matter.urgency] || URGENCY_BAR[URGENCY.next]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[12px] font-medium text-white truncate">{matter.client || matter.ref || "No reference yet"}</span>
          {matter.client && matter.ref && (
            <span className="hidden sm:inline text-[10px] font-mono text-slate-400 shrink-0">{matter.ref}</span>
          )}
        </div>
        <p className="text-[10px] text-slate-400 truncate mt-0.5">{first.sentence}</p>
      </div>
      <div className="flex flex-wrap justify-end gap-1 max-w-[45%] pt-0.5">
        {[first, ...rest].map((s, i) => (
          <button
            key={`${s.label}-${i}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(s, matter); }}
            title={s.sentence}
            className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap transition-colors ${i === 0 ? URGENCY_CHIP[s.urgency] || URGENCY_CHIP[URGENCY.next] : QUIET_CHIP}`}
            data-testid="today-step-chip"
          >
            {s.label}
          </button>
        ))}
      </div>
      <ChevronRight className="w-3 h-3 mt-1 text-slate-600 group-hover:text-slate-400 shrink-0" />
    </div>
  );
}

export default function TodayWithMate({ onOpenMate }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [nextSteps, setNextSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patrolRunning, setPatrolRunning] = useState(false);
  const [dataAsOf, setDataAsOf] = useState(null);
  const [bucketErrors, setBucketErrors] = useState(null);
  // BM-H640: the signed-in person's compliance tasks (/compliance/my-tasks).
  const [compliance, setCompliance] = useState(null);
  const [complianceError, setComplianceError] = useState(null);
  const [showAllActions, setShowAllActions] = useState(false);

  const userStr = localStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : null;
  const userEmail = (userObj?.user_email || userObj?.email || userObj?.ms_email || sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "").toLowerCase().trim();
  const feeEarner = getUserFeeEarner(userEmail);
  const supervisedInitials = SUPERVISED_FEE_EARNERS[userEmail] || (feeEarner ? [feeEarner.initials] : null);
  const userRole = getUserRole(userEmail);

  // Credit Control bucket: ACCOUNTS (May) only — directors see this via Directors Lounge, not Today card
  const canSeeCreditControl = userRole === ROLES.ACCOUNTS;

  const fetchQueue = async () => {
    setLoading(true);
    // BM-H640: fetched on its own so a compliance failure cannot blank the
    // panel; a failure goes to the warning strip, never an empty group.
    const complianceLoad = fetchComplianceTasks(axios.get, API, headers()).then((c) => {
      setCompliance(c.error ? null : c);
      setComplianceError(c.error || null);
    });
    try {
      const [queueRes, subsRes] = await Promise.all([
        axios.get(`${API}/mate/today-queue`, { headers: headers() }),
        axios.get(`${API}/submissions`, { params: { limit: 200 }, headers: headers() }).catch(() => ({ data: { submissions: [] } })),
      ]);
      setData(queueRes.data.buckets);
      setDataAsOf(queueRes.data.data_as_of || null);
      setBucketErrors(queueRes.data.errors || null);
      // Surface matters awaiting conflict check as next steps
      const subs = subsRes.data?.submissions || subsRes.data || [];
      const awaitingCC = subs.filter(s => {
        if (["archived", "deleted", "closed"].includes(s.status)) return false;
        if (s.status !== "submitted") return false;
        const d = s.data || {};
        const hasConflict = s.conflict_check_id || d.conflict_check_id || s.status === "open" || s.status === "active";
        if (hasConflict) return false;
        // Fix 3: matters with a full reference are already open, past conflict check stage
        const isLikelyOpen = s.matter_reference && /BL\.\w+\.\d{3}\.\d{4}/.test(s.matter_reference);
        if (isLikelyOpen) return false;
        // Accounts role sees all submissions without fee earner filtering
        if (userRole === ROLES.ACCOUNTS) return true;
        // Fee earner filter — directors AND fee earners use supervisedInitials
        // so directors only see their own practice area matters, not the whole firm
        const filterInitials = supervisedInitials || (feeEarner ? [feeEarner.initials] : null);
        if (filterInitials) {
          const fe = (d.fee_earner || d.Q6 || d.assigned_to || "").toLowerCase();
          const mattRef = (s.matter_reference || "").toUpperCase();
          const matches = filterInitials.some(ini =>
            fe.includes(ini.toLowerCase()) ||
            fe.includes(ini === 'VEC' ? 'vanessa' : ini === 'RM' ? 'rebekah' : ini === 'HD' ? 'himanie' : ini.toLowerCase()) ||
            mattRef.includes(`.${ini}.`)
          );
          if (!matches) return false;
        }
        return true;
      }).map(s => {
        const d = s.data || {};
        return {
          id: s.id || s._id,
          matter_reference: s.matter_reference || d.matter_reference || s.provisional_id || "",
          client_name: d.client_name || d.Q1 || s.primary_label || "",
          fee_earner: d.fee_earner || d.Q6 || d.assigned_to || "",
          step: "Awaiting Conflict Check",
        };
      });
      setNextSteps(awaitingCC);
    } catch {
      setData(null);
      setNextSteps([]);
      setDataAsOf(null);
      setBucketErrors(null);
    }
    await complianceLoad;
    setLoading(false);
  };

  // Render the FE-side aggregation timestamp if the BE didn't supply one
  // (older deployments). Lets the user see *some* freshness signal so a
  // stale "All clear" can never be silent.
  const freshnessLabel = (() => {
    const iso = dataAsOf;
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short",
      });
    } catch {
      return iso;
    }
  })();

  useEffect(() => { fetchQueue(); }, []);

  const runPatrol = async () => {
    setPatrolRunning(true);
    try {
      await axios.post(`${API}/mate/patrol/run`, {}, { headers: headers() });
      toast.success("Patrol scan complete");
      fetchQueue();
    } catch {
      toast.error("Patrol scan failed");
    }
    setPatrolRunning(false);
  };

  const dismissAlert = async (alertId) => {
    try {
      await axios.post(`${API}/mate/alerts/${alertId}/dismiss`, {}, { headers: headers() });
      fetchQueue();
    } catch {
      toast.error("Failed to dismiss alert");
    }
  };

  if (loading) {
    return (
      <div className="bg-navy-card border border-navy-border rounded-lg p-6" data-testid="today-mate-panel">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-teal" />
          <span className="text-sm font-bold text-white">Today with Bonsai Mate</span>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-teal" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const creditControlVisible = canSeeCreditControl && (data.credit_control?.record_count > 0);

  // Pipeline action items.  The backend (mate.py `_supervised_match`)
  // already filters by the caller's supervised initials via resolve_staff
  // alias resolution.  The previous FE re-filter was strictly narrower
  // than the BE filter — it dropped items where (a) matter_reference was
  // empty (provisional matters) AND (b) the fee_earner was a full name
  // ("Vanessa Challess") rather than initials.  That is exactly the case
  // a draft FORF lands in before the SP folder is created, which is why
  // Vanessa was seeing "All clear" for matters she actually owned.
  //
  // The BE response is now the single source of truth; we only filter
  // here for the legacy fallback path where the BE returned no items
  // because it raised — see bucketErrors banner below.
  const filteredPipelineItems = data.pipeline_actions?.items || [];

  // BM-H640: bucket errors from the queue plus a failed compliance read.
  const shownErrors = { ...(bucketErrors || {}), ...(complianceError ? { compliance: complianceError } : {}) };
  const hasErrors = Object.keys(shownErrors).length > 0;
  const complianceTasks = compliance?.tasks || [];

  // BM-H3775: Compliance, Pipeline Actions and Next Steps were three lists
  // of the same matters. One list now, a row per matter, most urgent first,
  // holding only what can be done now (Vanessa: "what do i need to do
  // today/right now"); matters waiting on someone else are only counted.
  const actions = buildTodayActions({
    pipelineItems: filteredPipelineItems,
    complianceTasks,
    awaitingConflict: nextSteps,
  });
  const ACTIONS_SHOWN = 5;
  const shownMatters = showAllActions ? actions.matters : actions.matters.slice(0, ACTIONS_SHOWN);

  // Each step opens where it is done: a pipeline step through
  // openPipelineAction (BM-H3734), a compliance task through its own link,
  // a submission awaiting its conflict check through its submission page.
  const openStep = (step, matter) => {
    const open = step.open || {};
    if (open.kind === "pipeline") {
      return openPipelineAction({
        item: open.item, axios, apiBase: API, navigate, onError: (msg) => toast.error(msg),
      });
    }
    if (open.kind === "link" && open.link) return navigate(open.link);
    if (open.kind === "submission" && open.id) return navigate(`/submission/${open.id}`);
    const page = matterPagePath(matter?.ref);
    if (page) return navigate(page);
    return toast.error("There is nowhere to open for this step yet.");
  };

  const totalItems = actions.stepCount + actions.personal.length
    + (data.deadlines?.overdue_count || 0) + (data.stale_matters?.count || 0) + (data.disbursement_moa?.count || 0)
    + (creditControlVisible ? (data.credit_control?.record_count || 0) : 0)
    + (data.completions?.count || 0)
    + (data.patrol_alerts?.count || 0) + (data.my_matters?.count || 0);

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl overflow-hidden" data-testid="today-mate-panel">
      {/* Header — the AI band (cm-hero), slim so the list stays above the fold. */}
      <div className="cm-hero relative overflow-hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div aria-hidden className="agents-orb absolute -top-24 right-10 w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(236,72,153,0.28), transparent 65%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="relative w-9 h-9 rounded-xl flex items-center justify-center ring-1 ring-white/20 shadow-lg shadow-fuchsia-900/40"
            style={{ background: "linear-gradient(135deg, #6366f1, #a855f7 55%, #ec4899)" }}>
            <Sparkles className="w-[18px] h-[18px] text-white" />
            <Sparkles className="agents-twinkle absolute -top-1 -right-1 w-3 h-3 text-amber-200" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold agents-title">Today with Bonsai Mate</span>
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider text-white/90 cm-glass ring-1 ring-white/20">
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
            </div>
            <div className="text-[10px] text-indigo-100/70">
              <span className="tabular-nums">{totalItems}</span> items
              {freshnessLabel && (
                <span
                  className="ml-1.5"
                  title="Data refreshed at this time (UTC)"
                  data-testid="today-data-as-of"
                >
                  · as of {freshnessLabel}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="relative flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={runPatrol} disabled={patrolRunning}
            className="h-7 w-7 p-0 text-indigo-100/70 hover:text-white cm-glass-hover" title="Run patrol scan" data-testid="run-patrol-btn">
            {patrolRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchQueue}
            className="h-7 w-7 p-0 text-indigo-100/70 hover:text-white cm-glass-hover" title="Refresh" data-testid="refresh-today-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {onOpenMate && (
            <Button size="sm" onClick={onOpenMate}
              className="h-7 px-3 ml-1 text-[11px] font-semibold text-white rounded-full border-0 shadow-md shadow-fuchsia-900/40 hover:brightness-110"
              style={{ background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899)" }}
              data-testid="ask-mate-btn">
              <Sparkles className="w-3 h-3 mr-1" /> Ask Mate
            </Button>
          )}
        </div>
      </div>

      {hasErrors && (
        <div
          className="px-3 py-1.5 bg-status-amber-dim/60 text-status-amber text-[10px] border-b border-navy-border/40 space-y-1"
          data-testid="today-bucket-errors"
        >
          <div>
            Partial load — some buckets failed: {Object.keys(shownErrors).join(", ")}.
            Refresh to retry.
          </div>
          {Object.keys(shownErrors).map((bucketName) => (
            <details
              key={bucketName}
              className="text-[10px]"
              data-testid={`today-bucket-error-${bucketName}`}
            >
              <summary className="cursor-pointer text-status-amber hover:text-white">
                {bucketName}
              </summary>
              <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-words font-mono text-[10px] text-txt-muted bg-navy/60 border border-navy-border/40 rounded p-2">
                {String(shownErrors[bucketName])}
              </pre>
            </details>
          ))}
        </div>
      )}

      <div className="divide-y divide-navy-border/40">
        {/* Patrol Alerts */}
        {data.patrol_alerts?.count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={Shield} iconColor="text-status-red" title="Patrol Alerts" count={data.patrol_alerts.count} badge="bg-status-red-dim text-status-red" />
            {(data.patrol_alerts?.alerts || []).slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-1">
                <div className="flex-1">
                  <ActionRow text={a.title} detail={a.detail} severity={a.severity}
                    onClick={() => a.matter_reference ? navigate(matterPagePath(a.matter_reference)) : null} />
                </div>
                <Button variant="ghost" size="sm" onClick={() => dismissAlert(a.id)}
                  className="h-5 w-5 p-0 text-slate-600 hover:text-slate-300 shrink-0" data-testid={`dismiss-patrol-${a.id}`}>
                  <X className="w-2.5 h-2.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* BM-H3775: tasks with no matter (the person's own training, the
            month's file review sampling), kept apart from the matter list. */}
        {actions.personal.length > 0 && (
          <div className="px-3 py-2" data-testid="today-personal-strip">
            <div className="flex items-center gap-1.5 mb-1">
              <GraduationCap className="w-3.5 h-3.5 text-teal" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">For you</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1.5">
              {actions.personal.map((p, i) => (
                <button
                  key={`${p.label}-${i}`}
                  type="button"
                  onClick={() => openStep(p, null)}
                  className={`flex items-center gap-2 text-left px-2.5 py-1 rounded border transition-colors hover:bg-navy-hover/60 ${p.urgency === URGENCY.overdue ? "border-status-red/60" : "border-navy-border"}`}
                  data-testid="today-personal-item"
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.urgency === URGENCY.overdue ? "bg-status-red" : "bg-status-amber"}`} />
                  <span className="text-[11px] text-white">{p.sentence}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* BM-H3775: one actions list, a row per matter. Replaces the
            Compliance, Next Steps and Pipeline Actions groups. */}
        {actions.matters.length > 0 && (
          <div className="px-3 py-2.5" data-testid="today-actions-group">
            <BucketHeader icon={Zap} iconColor="text-teal" title="Do now" count={actions.matters.length}
              badge={actions.matters[0].urgency <= URGENCY.stopped ? "bg-status-red-dim text-status-red" : "bg-teal-dim text-teal"} />
            <div className="space-y-0.5">
              {shownMatters.map((m, i) => (
                <MatterActionRow key={`${m.ref || "no-ref"}-${i}`} matter={m} onOpen={openStep} />
              ))}
            </div>
            {actions.matters.length > ACTIONS_SHOWN && (
              <button
                type="button"
                onClick={() => setShowAllActions((v) => !v)}
                className="mt-1 ml-3.5 text-[10px] text-teal hover:text-white"
                data-testid="today-actions-show-all"
              >
                {showAllActions ? "Show fewer" : `Show ${actions.matters.length - ACTIONS_SHOWN} more`}
              </button>
            )}
          </div>
        )}

        {/* BM-H3775: waiting on the client, Thirdfort or a director is not a
            Today item; say how many, and where they are. */}
        {actions.waiting.length > 0 && (
          <div className="px-3 py-2 text-[10px] text-slate-400" data-testid="today-waiting-note">
            {actions.waiting.length} {actions.waiting.length === 1 ? "matter is" : "matters are"} waiting on someone else; see the pipeline below.
          </div>
        )}

        {/* My Matters */}
        {data.my_matters?.count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={Briefcase} iconColor="text-teal" title="My Matters" count={data.my_matters.count} badge="bg-teal-dim text-teal" />
            {(data.my_matters?.matters || []).slice(0, 5).map((m, i) => (
              <ActionRow key={i} text={m.client_name} detail={m.matter_reference} severity="low"
                onClick={() => navigate(matterPagePath(m.matter_reference) || "/pipeline")} />
            ))}
          </div>
        )}

        {/* Overdue Deadlines */}
        {data.deadlines?.overdue_count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={AlertTriangle} iconColor="text-status-red" title="Overdue Deadlines" count={data.deadlines.overdue_count} badge="bg-status-red-dim text-status-red" />
            {(data.deadlines?.overdue || []).slice(0, 4).map((dl, i) => (
              <ActionRow key={i} text={dl.title} detail={`Due ${dl.due_date} — ${dl.matter_ref}`} severity="high"
                onClick={() => navigate("/deadlines")} />
            ))}
          </div>
        )}

        {/* Upcoming Deadlines */}
        {data.deadlines?.upcoming_count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={Clock} iconColor="text-status-amber" title="Coming Up" count={data.deadlines.upcoming_count} badge="bg-status-amber-dim text-status-amber" />
            {(data.deadlines?.upcoming || []).slice(0, 3).map((dl, i) => (
              <ActionRow key={i} text={dl.title} detail={`Due ${dl.due_date} — ${dl.matter_ref}`} severity="medium"
                onClick={() => navigate("/deadlines")} />
            ))}
          </div>
        )}

        {/* Stale Matters */}
        {data.stale_matters?.count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={AlertCircle} iconColor="text-status-amber" title="Dormant Matters" count={data.stale_matters.count} badge="bg-status-amber-dim text-status-amber" />
            {(data.stale_matters?.matters || []).slice(0, 3).map((m, i) => (
              <ActionRow key={i} text={m.client_name} detail={`${m.matter_reference} — ${m.days_inactive}d inactive`} severity="medium"
                onClick={() => navigate(`/matters`)} />
            ))}
          </div>
        )}

        {/* BM-H5023: disbursement quotes awaiting money on account */}
        {data.disbursement_moa?.count > 0 && (
          <div className="px-3 py-2.5" data-testid="today-disbursement-moa">
            <BucketHeader icon={AlertCircle} iconColor="text-status-amber" title="Disbursement Quotes Awaiting MOA" count={data.disbursement_moa.count} badge="bg-status-amber-dim text-status-amber" />
            {(data.disbursement_moa?.quotes || []).slice(0, 3).map((q) => (
              <ActionRow key={q.id} text={`${q.supplier}: ${q.description}`}
                detail={`${q.matter_reference} — £${Number(q.outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })} MOA needed, quoted ${q.quoted_on}`}
                severity="medium"
                onClick={() => { const page = matterPagePath(q.matter_reference); if (page) navigate(page); }} />
            ))}
          </div>
        )}

        {/* Credit Control — ACCOUNTS only */}
        {creditControlVisible && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={PoundSterling} iconColor="text-teal" title="Credit Control" count={data.credit_control.record_count} badge="bg-teal-dim text-teal" />
            <p className="text-[10px] text-slate-400 px-2.5 mb-1">Outstanding: £{(data.credit_control.total_outstanding || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            {(data.credit_control?.records || []).slice(0, 3).map((r, i) => (
              <ActionRow key={i} text={r.client} detail={`${r.matter_ref} — £${(r.outstanding || 0).toLocaleString()} (${r.stage})`} severity="medium"
                onClick={() => navigate(`/credit-control/${r.id}`)} />
            ))}
          </div>
        )}

        {/* Upcoming Completions */}
        {data.completions?.count > 0 && (
          <div className="px-3 py-2.5">
            <BucketHeader icon={Calendar} iconColor="text-blue-400" title="Completions This Week" count={data.completions.count} badge="bg-blue-900 text-blue-300" />
            {(data.completions?.entries || []).slice(0, 3).map((c, i) => (
              <ActionRow key={i} text={c.buyer || c.seller} detail={`${c.matter_ref} — ${c.completion_date}`} severity="low"
                onClick={() => navigate("/completions-book")} />
            ))}
          </div>
        )}

        {/* Empty state — only show "All clear" if every bucket loaded */}
        {totalItems === 0 && !hasErrors && (
          <div className="px-4 py-8 text-center" data-testid="today-all-clear">
            <CheckCircle className="w-8 h-8 text-teal mx-auto mb-2" />
            <p className="text-sm text-white font-medium">All clear</p>
            <p className="text-[10px] text-slate-400">No urgent items right now. Nice work.</p>
            {freshnessLabel && (
              <p className="text-[9px] text-slate-500 mt-2">
                Checked at {freshnessLabel}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
